/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { CheckCircle2, Clock, Hourglass, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { cn } from '@/lib/utils';
import { api } from '../../../api';
import type { Auction, AuctionEventType, AuctionStatus } from '../../../api/areas/market';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { FactRows, SplitPane, Stepper, Timeline, type StepState } from '../../../components/flow';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { RefreshButton } from '../../registry/shared';
import { AuctionLinks, counted, useAuctionRows } from '../module';
import { AUCTION_STATUS, CustomerRef, MarketBadge, marketEnum, money } from '../shared';

const AFTER_CLOSE: AuctionStatus[] = ['CLOSED', 'SETTLING', 'SETTLED', 'FAILED', 'UNSOLD'];
const SETTLEMENT_EVENTS: AuctionEventType[] = ['AUCTION_CLOSED', 'WINNER_SELECTED', 'AUCTION_UNSOLD', 'SETTLEMENT_STARTED', 'PAYMENT_SETTLED', 'DOMAIN_ASSIGNED', 'SETTLEMENT_FAILED', 'RESULT_OVERRIDDEN'];

/** The picked session's settlement: the money, the four steps and the events that moved it. */
function SettlementDetail({ id }: { id: string }) {
  const { t } = useTranslation('market');
  const result = useApi((signal) => api.getAuction(id, { signal }), [id]);
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('auction.loadError')} />;
  const a = result.data;
  const has = (e: AuctionEventType) => a.events.some((x) => x.event_type === e);
  const failed = a.status === 'FAILED' || has('SETTLEMENT_FAILED');
  const step = (done: boolean, current: boolean): StepState => (done ? 'done' : failed && current ? 'blocked' : current ? 'current' : 'todo');
  const paid = Boolean(a.capture_entry_id) || has('PAYMENT_SETTLED');
  const assigned = Boolean(a.domain_id) || has('DOMAIN_ASSIGNED');
  const events = a.events.filter((e) => SETTLEMENT_EVENTS.includes(e.event_type)).reverse();
  return (
    <Section title={t('au.settlement.detailTitle', { domain: a.domain_name })} description={<MarketBadge group="auctionStatus" map={AUCTION_STATUS} value={a.status} />}>
      <div className="space-y-5">
        <FactRows
          rows={[
            [t('auction.finalPrice'), a.final_price ? <b key="p">{money(a.final_price, a.currency)}</b> : '—'],
            [t('auction.winner'), a.winner_customer_id ? <CustomerRef key="w" id={a.winner_customer_id} name={a.winner_name} /> : '—'],
            [
              t('au.settlement.hold'),
              a.winner_hold_id ? (
                <Link key="h" to={`/holds/${encodeURIComponent(a.winner_hold_id)}`} className="text-primary hover:underline">
                  {t('au.settlement.openHold')}
                </Link>
              ) : (
                '—'
              ),
            ],
            [
              t('au.settlement.capture'),
              a.capture_entry_id ? (
                <Link key="c" to={`/ledger/journal/${encodeURIComponent(a.capture_entry_id)}`} className="text-primary hover:underline">
                  {t('au.settlement.openEntry')}
                </Link>
              ) : (
                '—'
              ),
            ],
            [
              t('auction.order'),
              a.order_id ? (
                <Link key="o" to={`/orders/${encodeURIComponent(a.order_id)}`} className="text-primary hover:underline">
                  {t('auction.openOrder')}
                </Link>
              ) : (
                '—'
              ),
            ],
            [t('auction.settled'), a.settled_at ? formatDateTime(a.settled_at) : '—'],
          ]}
        />
        <Stepper
          className="md:flex-col md:[&>li[aria-hidden]]:hidden"
          label={t('au.settlement.flow')}
          steps={[
            { id: 'winner', label: t('au.flow.winner'), state: step(Boolean(a.winner_bid_id), !a.winner_bid_id) },
            { id: 'paid', label: t('au.flow.paid'), state: step(paid, Boolean(a.winner_bid_id) && !paid) },
            { id: 'assigned', label: t('au.flow.assigned'), state: step(assigned, paid && !assigned) },
          ]}
        />
        <Timeline
          empty={t('au.settlement.noEvents')}
          items={events.map((e) => ({
            id: e.id,
            tone: e.event_type === 'SETTLEMENT_FAILED' ? 'crit' : e.event_type === 'RESULT_OVERRIDDEN' ? 'warn' : 'info',
            title: marketEnum('auctionEvent', e.event_type),
            time: formatDateTime(e.occurred_at),
            body: typeof e.payload.reason === 'string' ? e.payload.reason : undefined,
          }))}
        />
        <Link to={`/auctions/${encodeURIComponent(a.id)}`} className="inline-block text-sm font-medium text-primary hover:underline">
          {t('au.settlement.openSession')}
        </Link>
      </div>
    </Section>
  );
}

/** `/auctions?tab=settlement`, reference 68: sessions after closing, and one session's settlement beside the list. */
export function AuctionSettlementPage() {
  const { t } = useTranslation('market');
  const [params, setParams] = useSearchParams();
  const rows = useAuctionRows();
  const header = <PageHeader title={t('au.settlement.title')} subtitle={t('au.settlement.subtitle')} actions={<RefreshButton onClick={rows.reload} />} />;
  if (rows.status !== 'success') {
    return (
      <>
        {header}
        <AuctionLinks here="settlement" />
        {rows.status === 'error' ? <DataError error={rows.error} onRetry={rows.reload} title={t('auctions.loadError')} /> : <Loading rows={5} />}
      </>
    );
  }
  const all = rows.data.items;
  const list = all.filter((a) => AFTER_CLOSE.includes(a.status));
  const picked = list.find((a) => a.id === params.get('id')) ?? list[0];
  const pick = (a: Auction) => {
    const p = new URLSearchParams(params);
    p.set('id', a.id);
    setParams(p, { replace: true });
  };
  const n = (s: AuctionStatus) => counted(all, all.filter((a) => a.status === s).length);
  const columns: Column<Auction>[] = [
    {
      key: 'domain',
      header: t('au.bids.session'),
      cell: (a) => (
        <button type="button" onClick={() => pick(a)} className="text-left font-semibold text-primary hover:underline">
          {a.domain_name}
        </button>
      ),
    },
    { key: 'winner', header: t('auction.winner'), className: 'min-w-40 max-w-56 whitespace-normal', cell: (a) => (a.winner_customer_id ? <CustomerRef id={a.winner_customer_id} name={a.winner_name} /> : <span className="text-muted-foreground">—</span>) },
    { key: 'price', header: t('auction.finalPrice'), numeric: true, cell: (a) => (a.final_price ? money(a.final_price, a.currency) : '—') },
    { key: 'status', header: t('auctions.status'), cell: (a) => <MarketBadge group="auctionStatus" map={AUCTION_STATUS} value={a.status} /> },
  ];
  return (
    <>
      {header}
      <AuctionLinks here="settlement" />
      <div className="space-y-5">
        <StatGrid>
          <StatCard label={t('au.settlement.awaiting')} icon={Hourglass} tone="pending" value={n('CLOSED')} hint={t('au.settlement.awaitingHint')} />
          <StatCard label={t('au.settlement.settling')} icon={Clock} tone="info" value={n('SETTLING')} hint={t('au.settlement.settlingHint')} />
          <StatCard label={t('au.settlement.settled')} icon={CheckCircle2} tone="ok" value={n('SETTLED')} hint={t('au.settlement.settledHint')} />
          <StatCard label={t('au.settlement.failed')} icon={XCircle} tone={all.some((a) => a.status === 'FAILED') ? 'crit' : 'neutral'} value={n('FAILED')} hint={t('au.settlement.failedHint')} />
        </StatGrid>
        <SplitPane
          list={
            <Section flush title={t('au.settlement.list')} description={t('au.settlement.listHint')}>
              <DataTable
                columns={columns}
                rows={list}
                rowKey={(a) => a.id}
                onRowClick={pick}
                rowClassName={(a) => cn(a.id === picked?.id && 'bg-accent/60')}
                empty={<EmptyState title={t('au.settlement.none')} />}
              />
            </Section>
          }
          detail={picked ? <SettlementDetail key={picked.id} id={picked.id} /> : null}
        />
        <Callout tone="info" title={t('au.settlement.noEscrowTitle')}>
          {t('au.settlement.noEscrow')}
        </Callout>
      </div>
    </>
  );
}
